import React, { useRef, useState, useMemo, useEffect } from 'react'
import shortid from 'shortid'
import { useDispatch, useSelector } from 'react-redux'

import Input from './input'
import Button, { ButtonRow } from './button'
import TopScrollbar from './top-scrollbar'
import { GeneratorFields } from '../api-client/api-types'
import styles from '../component-styles/campaign-code-generator.module.scss'
import { paginateData, sortData } from '../helpers'
import { getAnchorFromString } from '../helpers/campaign-codes'
import Pagination from './pagination'
import { SelectBoxSimple } from './select-box'
import OrderArrow from './order-arrow'
import { getShortLinkCandidatesGQL } from '../store/actions'
import { CampaignCodesState } from '../store/campaign-codes-reducer'
// import { maxShortLinkAliasLength } from '../api-client/constants'
import EditableShortLinkInput from './editable-short-link-input'
import RequestBrandedDomainModal from './request-branded-domain-modal'

const getUnusedShortLink = (allLinks, usedLinks) => {
  const unused = allLinks.filter((i) => usedLinks.indexOf(i) === -1)
  if (unused.length > 0) {
    return unused.pop()
  }
  return ''
}

interface CampaignCodesPreviewTableProps {
  form: any
  codes: any
  url?: string[]
  onUpdateSelection: any
  linkType: string
  onRefreshShortLinks: (any) => void
}

export default function CampaignCodeCreatePreviewTable({
  form,
  codes,
  url = [],
  onUpdateSelection,
  linkType,
  onRefreshShortLinks,
}: CampaignCodesPreviewTableProps): React.ReactElement | null {
  const [showBrandedLinkModal, setShowBrandedLinkModal] = useState(false)
  const [previewCodes, setShowPreviewCodes] = useState<any>(codes)
  const [activePage, setActivePage] = useState(1)
  const [pages, setPages] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(50)
  const [allChecked, setAllChecked] = useState(true)
  const [sortKey, setSortKey] = useState('url')
  const [orderAsc, setOrderAsc] = useState(false)
  const ref = useRef(null)
  const [codesWithShortLinks, setCodesWithShortLinks] = useState({})
  const dispatch = useDispatch()
  const { shortLinkIdList } = useSelector(
    (s: any): CampaignCodesState => s.campaignCodes,
  )

  useEffect(() => {
    if (codes.length > shortLinkIdList.length) {
      const links = codes.length - shortLinkIdList.length
      dispatch(getShortLinkCandidatesGQL({ links }))
    } else {
      const mapped = {}
      codes.forEach((link) => {
        if (
          codesWithShortLinks[link.code] &&
          codesWithShortLinks[link.code] !== ''
        ) {
          mapped[link.code] = codesWithShortLinks[link.code]
        } else {
          const usedLinks: string[] = []
          const merged = { ...codesWithShortLinks, ...mapped }
          Object.keys(merged).forEach((key) => {
            if (merged[key] !== '') {
              usedLinks.push(merged[key])
            }
          })
          const useShortLink = getUnusedShortLink(shortLinkIdList, usedLinks)
          mapped[link.code] = useShortLink
        }
      })
      setCodesWithShortLinks(mapped)
      onRefreshShortLinks(mapped)
    }
  }, [shortLinkIdList])

  const setSortOrder = (key: string): void => {
    if (key === sortKey) {
      setOrderAsc(!orderAsc)
    } else {
      setSortKey(key)
      setOrderAsc(true)
    }
    // Go to the start of pagination
    setActivePage(1)
  }

  const orderedData = useMemo(() => {
    let fn
    // eslint-disable-next-line
    // debugger
    if (sortKey.search(/[0-9]{1,9}-/gi) === 0) {
      const index = sortKey.split('-')[0]
      fn = (row) => {
        return row.pDfs[index][1] || ''
      }
    }

    const filtered = sortData([...previewCodes], sortKey, orderAsc, fn)
    setPages(Math.ceil(filtered.length / rowsPerPage))
    return paginateData(filtered, rowsPerPage)
  }, [sortKey, orderAsc, previewCodes, rowsPerPage])

  if (!form || previewCodes.length === 0) {
    return <></>
  }

  return (
    <>
      <div className={styles.tableContainer}>
        <TopScrollbar>
          <table ref={ref} className={styles.table}>
            <thead>
              <tr>
                <th className={styles.checkboxCell}>
                  <Input
                    type="checkbox"
                    id="allNone"
                    name="allNone"
                    checked={allChecked}
                    className={styles.selectItem}
                    label=" "
                    onChange={(
                      e: React.ChangeEvent<HTMLInputElement>,
                    ): void => {
                      const { checked } = e.target as HTMLInputElement
                      setAllChecked(checked)
                      const setCodes = previewCodes.map((item) => {
                        return {
                          ...item,
                          selected: checked,
                        }
                      })
                      setShowPreviewCodes(setCodes)
                    }}
                  />
                </th>
                {linkType === 'short' && (
                  <th className={styles.shortLinkCell}>Short link</th>
                )}
                {url.length > 1 && (
                  <th key={shortid.generate()} className={styles.otherColumn}>
                    <Button
                      className={styles.headerButton}
                      onClick={(): void => {
                        setSortOrder('url')
                      }}
                      type="none"
                    >
                      Landing page URL
                      <OrderArrow
                        className={styles.orderArrow}
                        currentKey="url"
                        sortKey={sortKey}
                        orderAsc={orderAsc}
                      />
                    </Button>
                  </th>
                )}
                {form &&
                  form.paramDefs.map(
                    (
                      field: GeneratorFields,
                      index: number,
                    ): React.ReactElement | null => {
                      if (previewCodes[0].pDfs[index][0] === '') {
                        return null
                      }
                      return (
                        <th
                          key={shortid.generate()}
                          className={styles.otherColumn}
                        >
                          <Button
                            className={styles.headerButton}
                            onClick={(): void => {
                              setSortOrder(`${index}-${field.fieldName}`)
                            }}
                            type="none"
                          >
                            {field.fieldName}
                            {field.metaParameter ? (
                              <b className={styles.optional}> (meta)</b>
                            ) : (
                              ''
                            )}
                            <OrderArrow
                              className={styles.orderArrow}
                              currentKey={`${index}-${field.fieldName}`}
                              sortKey={sortKey}
                              orderAsc={orderAsc}
                            />
                          </Button>
                        </th>
                      )
                    },
                  )}
              </tr>
            </thead>
            <tbody>
              {orderedData.length > 0 &&
                orderedData[activePage - 1] &&
                orderedData[activePage - 1].map(
                  (field: any, index): React.ReactElement => {
                    const useAnchor = getAnchorFromString(field.tC)
                    const sLink =
                      Object.prototype.hasOwnProperty.call(
                        codesWithShortLinks,
                        field.code,
                      ) && codesWithShortLinks[field.code]
                        ? codesWithShortLinks[field.code]
                        : ''
                    const { length } = orderedData[activePage - 1]

                    return (
                      <tr
                        key={JSON.stringify(field)}
                        className={styles.otherColumn}
                      >
                        <td className={styles.checkboxCell}>
                          <Input
                            type="checkbox"
                            id={shortid.generate()}
                            name={shortid.generate()}
                            checked={field.selected}
                            className={styles.selectItem}
                            label=" "
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ): void => {
                              const { checked } = e.target as HTMLInputElement
                              const setCodes = previewCodes.map((item) => {
                                if (field.code === item.code) {
                                  return {
                                    ...item,
                                    selected: checked,
                                  }
                                }
                                return item
                              })
                              setShowPreviewCodes(setCodes)
                              onUpdateSelection(setCodes)
                            }}
                          />
                        </td>
                        {linkType === 'short' && (
                          <td className={styles.shortLinkCell}>
                            <EditableShortLinkInput
                              canChangeShortLink={false}
                              showDropDownOnTop={
                                length === index + 1 || length - 2 === index
                              }
                              setShowBrandedLinkModal={setShowBrandedLinkModal}
                              defaultKey={`${field.code}${sLink}`}
                              shortLink={sLink}
                              setShortLink={(linkId) => {
                                const mapped = { ...codesWithShortLinks }
                                mapped[field.code] = linkId
                                setCodesWithShortLinks(mapped)
                                onRefreshShortLinks(mapped)
                                // dispatch(getShortLinkCandidatesGQL({ links: 1}))
                              }}
                            />
                          </td>
                        )}
                        {url.length > 1 && (
                          <td>
                            {field.url}
                            {useAnchor}
                          </td>
                        )}
                        {field.pDfs.map((item) => {
                          if (item[0] === '') {
                            return null
                          }
                          return <td key={shortid.generate()}>{item[0]}</td>
                        })}
                      </tr>
                    )
                  },
                )}
            </tbody>
          </table>
        </TopScrollbar>
      </div>
      <ButtonRow className={styles.paginationRow}>
        <Pagination
          pages={pages}
          activePage={activePage}
          onChange={(index: number) => {
            setActivePage(index)
          }}
        />
        <span className={styles.rowsLabelWrapper}>
          <span className={styles.rowsLabel}>Show</span>
          <SelectBoxSimple
            name="rows-per-page"
            value={rowsPerPage}
            onChange={(rows) => {
              setRowsPerPage(rows)
              setActivePage(1)
            }}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="150">150</option>
          </SelectBoxSimple>
        </span>
      </ButtonRow>
      {showBrandedLinkModal && (
        <RequestBrandedDomainModal
          onHideModal={() => {
            setShowBrandedLinkModal(false)
          }}
        />
      )}
    </>
  )
}
